import axios from "axios"

/**
 * Analyze Excel file headers and suggest mapping
 * @param {string} token - Auth token
 * @param {File} file - Excel file
 * @returns {Promise<Object>} - Headers and suggested mapping
 */
export const analyzeExcelFile = async (token, file) => {
  const formData = new FormData()
  // The backend expects the file field to be named 'file'
  formData.append("file", file)

  const response = await axios.post("/api/myteconstruction/cost-codes/import/analyze", formData, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to analyze Excel file")
  }

  return response.data.data
}

/**
 * Start import job
 * @param {string} token - Auth token
 * @param {File} file - Excel file
 * @param {Object} mapping - Field mapping
 * @returns {Promise<Object>} - Job ID
 */
export const startImportJob = async (token, file, mapping) => {
  const formData = new FormData()
  // The backend expects the file field to be named 'file'
  formData.append("file", file)
  formData.append("mapping", JSON.stringify(mapping))

  const response = await axios.post("/api/myteconstruction/cost-codes/import", formData, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to start import job")
  }

  return response.data.data
}

/**
 * Get import job status
 * @param {string} token - Auth token
 * @param {string} jobId - Import job ID
 * @returns {Promise<Object>} - Job status
 */
export const getImportJobStatus = async (token, jobId) => {
  const response = await axios.get(
    `/api/myteconstruction/cost-codes/import/${jobId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    }
  )

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to get import job status")
  }

  return response.data.data
}

/**
 * Commit import job
 * @param {string} token - Auth token
 * @param {string} jobId - Import job ID
 * @returns {Promise<Object>} - Commit results
 */
/**
 * Commit import job with optional overridden codes list
 * @param {string} token - Auth token
 * @param {string} jobId - Import job ID
 * @param {Array} [codes] - Optional array of cost code objects to commit
 * @returns {Promise<Object>} - Commit results
 */
export const commitImportJob = async (token, jobId, codes) => {
  // If codes provided, pass them in request body; otherwise send null
  const body = codes ? { codes } : null;
  const response = await axios.post(
    `/api/myteconstruction/cost-codes/import/${jobId}/commit`,
    body,
    { headers: { Authorization: `Bearer ${token}` } }
  )

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to commit import job")
  }

  return response.data.data
}
